/**
 * PendingDraftDecoration — visual "uncommitted" feedback while typing.
 *
 * V2.7.6 (2026-05-20): explicit-commit semantics. As the local user
 * types, the freshly-typed range stays faded grey UNTIL one of these
 * commit events fires:
 *
 *   - Enter pressed (paragraph break — newline_pause trigger fires
 *     immediately server-side, no time-based grace)
 *   - Editor loses focus (blur trigger)
 *   - Component unmounts / explicit clear via clearPendingDraft()
 *
 * No time-based maturity — Alex was burned by the previous heuristic
 * pushing mid-thought after a 3 sec pause. The decoration disappears
 * the moment YOU commit, not when the system thinks you've paused.
 */

import { Extension } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { ySyncPluginKey } from '@tiptap/y-tiptap'

const PLUGIN_KEY = new PluginKey<PendingState>('pendingDraftDecoration')
const CLEAR_META = 'pendingDraftDecoration:clear'

interface PendingState {
  from: number | null
  to: number | null
}

const EMPTY_STATE: PendingState = { from: null, to: null }

export const PendingDraftDecoration = Extension.create({
  name: 'pendingDraftDecoration',

  addProseMirrorPlugins() {
    return [
      new Plugin<PendingState>({
        key: PLUGIN_KEY,
        state: {
          init: () => EMPTY_STATE,
          apply: (tr, prev) => {
            // Explicit clear (Enter pressed, blur, etc.)
            if (tr.getMeta(CLEAR_META)) {
              return EMPTY_STATE
            }

            // V2.7.10: remote y-sync is identified by ySyncPluginKey
            // meta OR by the absence of an addToHistory flag (local
            // edits set addToHistory=true via the keyboard binding,
            // remote y-tiptap inserts don't). Map existing range
            // through the remote insert; do NOT extend my pending
            // range with another author's chars (was the "blinking
            // grey" bug — Alex's range would suddenly cover Jascha's
            // simultaneous insertion).
            const isYSync =
              tr.getMeta(ySyncPluginKey) !== undefined ||
              tr.getMeta('y-sync$') !== undefined
            const isRemote =
              isYSync || tr.getMeta('addToHistory') === false

            if (isRemote) {
              if (prev.from === null) return prev
              return {
                from: tr.mapping.map(prev.from),
                to: tr.mapping.map(prev.to as number),
              }
            }

            if (!tr.docChanged) {
              return prev
            }

            // Local edit. Compute the newly-inserted range by walking
            // the transaction's steps. For simple insertions we get
            // exactly the typed span; for multi-step ops we union them.
            let editFrom: number | null = null
            let editTo: number | null = null
            tr.steps.forEach((step, idx) => {
              const map = tr.mapping.maps[idx]
              map.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
                if (editFrom === null || newStart < editFrom) editFrom = newStart
                if (editTo === null || newEnd > editTo) editTo = newEnd
              })
              void step
            })

            // Pure deletion? Just remap the existing pending range.
            if (editFrom === null || editTo === null || editTo <= editFrom) {
              if (prev.from === null) return prev
              return {
                from: tr.mapping.map(prev.from),
                to: tr.mapping.map(prev.to as number),
              }
            }

            // Local insertion — extend (or initialize) the pending range.
            // No time-based reset; only the explicit clear path drops it.
            if (prev.from !== null) {
              const mappedPrevFrom = tr.mapping.map(prev.from as number)
              const mappedPrevTo = tr.mapping.map(prev.to as number)
              return {
                from: Math.min(mappedPrevFrom, editFrom),
                to: Math.max(mappedPrevTo, editTo),
              }
            }
            return { from: editFrom, to: editTo }
          },
        },
        props: {
          decorations(state) {
            const pending = PLUGIN_KEY.getState(state)
            if (!pending || pending.from === null || pending.to === null) {
              return DecorationSet.empty
            }
            if (pending.to <= pending.from) {
              return DecorationSet.empty
            }
            return DecorationSet.create(state.doc, [
              Decoration.inline(pending.from, pending.to, {
                style: 'opacity: 0.45; transition: opacity 200ms;',
                'data-pending-draft': 'true',
              }),
            ])
          },
          // V2.7.8: Enter is back as a commit trigger. Server fires
          // newline_pause with the FULL accumulated burst (not just
          // the `\n` fragment), so the local pending decoration also
          // matures to normal color — visual match with the push.
          handleKeyDown(view, event) {
            if (event.key === 'Enter') {
              const tr = view.state.tr.setMeta(CLEAR_META, true)
              view.dispatch(tr)
            }
            return false
          },
        },
      }),
    ]
  },
})

/**
 * External helper to force-clear pending draft state (e.g. on blur).
 */
export function clearPendingDraft(view: {
  state: { tr: { setMeta: (key: string, value: unknown) => unknown } }
  dispatch: (tr: unknown) => void
}) {
  const tr = view.state.tr.setMeta(CLEAR_META, true)
  view.dispatch(tr)
}
